// status-effective-config-readers.ts
//
// Split out of status-renderer.ts (P13 Agent C — file crossed the 200-line
// budget once the operating-table work landed on top of Agent B's cost/limits
// work). Two tiny best-effort readers of `<control_root>/effective-config.json`
// (P4's already-written snapshot — see effective-config-loader.ts) used only
// by status-renderer.ts's payload builders: missing/unreadable file is never
// fatal for a read-only `status` command, it just means "show unlimited"/
// "show no ceiling" rather than crash.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import type { CostAttemptLimitsConfig } from "./mission-cost-accounting.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";

const DEFAULT_LIMITS: CostAttemptLimitsConfig = {
  max_cost_usd_per_mission: 0,
  max_attempts_per_mission: 0,
};

/** Reads the two §18-style ceiling keys from effective-config.json; missing/unreadable => same "0 = unlimited" default the schema itself uses. */
export async function readLimitsFromEffectiveConfig(
  controlRoot: string,
): Promise<CostAttemptLimitsConfig> {
  try {
    const raw = await fs.readFile(join(controlRoot, "effective-config.json"), "utf8");
    const parsed = JSON.parse(raw) as Partial<CostAttemptLimitsConfig>;
    return {
      max_cost_usd_per_mission:
        typeof parsed.max_cost_usd_per_mission === "number"
          ? parsed.max_cost_usd_per_mission
          : DEFAULT_LIMITS.max_cost_usd_per_mission,
      max_attempts_per_mission:
        typeof parsed.max_attempts_per_mission === "number"
          ? parsed.max_attempts_per_mission
          : DEFAULT_LIMITS.max_attempts_per_mission,
    };
  } catch {
    return DEFAULT_LIMITS;
  }
}

/** max_counted_failures from effective-config.json — best-effort, undefined when unreadable. */
export async function readMaxCountedFailures(controlRoot: string): Promise<number | undefined> {
  try {
    const raw = await fs.readFile(join(controlRoot, "effective-config.json"), "utf8");
    const parsed = JSON.parse(raw) as Partial<AutopilotConfig>;
    return typeof parsed.max_counted_failures === "number"
      ? parsed.max_counted_failures
      : undefined;
  } catch {
    return undefined;
  }
}
